import { SectorModel } from "../Models/SectorModel";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";
import { useCityEnricher } from "./useCityEnricher";

/**
 * =====================================================
 * Hook: useSectorEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich Sector domain data with related users.
 * Combines user information into SectorModel for detailed views.
 *
 * Responsibilities:
 * - Fetch and maintain related data (users)
 * - Track combined loading/error state
 * - Provide helper functions to enrich a single sector, by ID, or a list
 *
 * Notes:
 * - Returns a pure API for enrichment; no side effects beyond data fetching.
 * - Compatible with detail and list views for cities.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export function useSectorEnricher() {

    /**
             * Centralized audit enrichment hook
             * - Provides O(1) user resolution via precomputed Map
             * - Exposes audit enrichment helpers and status/error
             */
    const {
        enricherResponse: auditEnricherResponse,
        getEnrichedAudit,
    } = useAuditEnricher<SectorModel>();

    const { cityMap, countryMap, response : cityEnricherResponse } = useCityEnricher();


    /** =====================================================
     * Combined Loading & Error Status
     * =====================================================
     *
     * Combines status from all related hooks into a single ActionResponse:
     * - status: combined loading state (idle, loading, success, failed)
     * - error: first non-null error encountered
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(cityEnricherResponse.status, auditEnricherResponse.status),
        error: cityEnricherResponse.error ?? auditEnricherResponse.error
    };

    /** =====================================================
     * Public Helper Functions
     * =====================================================
     *
     * These functions enrich cities with:
     * - Audit info (created/updated by, timestamps)
     * - Related users (creator/updater)
     */


    // Enrich a list of cities
    const getEnrichedSectorMap = ( sectorMap: RecordMap<SectorModel>): RecordMap<SectorModel> => {
        return sectorMap.map<SectorModel>(getStrictEnrichedSector);
    }

    const getStrictEnrichedSector = (sector: SectorModel): SectorModel => {
        return getEnrichedAudit(sector);

    }

    // Enrich a single sector with audit/user info
    const getEnrichedSector = (sector?: SectorModel): SectorModel | undefined => {
        if (!sector) return undefined;
        return getStrictEnrichedSector(sector);
        
    }

    const getEnrichedSectorById = (id?: number | null): SectorModel | undefined => {
        if (!id) {
            return undefined;
        }
        const foundSector = cityMap.findMap(city =>
            city.sectors.find(sector => sector.id === id)
        );
        
        return getEnrichedSector(foundSector);
    }

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Provides all enriched data and helper functions
     * for consuming components.
     */
    return {
        sectorMap : cityMap.flatMapArray(city => city.sectors),
        cityMap,
        countryMap,
        response,
        getEnrichedSector,
        getEnrichedSectorById,
        getEnrichedSectorMap
    }
}